// Pure logic for the economy autopilot in the Director console (ADR 0027,
// APL, LW5): dial labels and values as staff read them, the targets against
// their bands, the agreement against the 80% exit, which actions a proposal
// offers, and normalising the loose JSON the API sends. No React and no
// imports, so `node --test` runs it directly (autopilotLogic.test.ts).

// ---------------------------------------------------------------- shapes

export const DIALS = ["npc_demand_pct", "op_take_scale", "contract_take_scale", "price_index", "rent_index", "arc_sink"] as const;
export type Dial = (typeof DIALS)[number];

export const DIAL_LABEL: Record<string, string> = {
  npc_demand_pct: "NPC demand",
  op_take_scale: "Op take scale",
  contract_take_scale: "Contract take scale",
  price_index: "Chop and store prices",
  rent_index: "Premises rent",
  arc_sink: "Arc sink intensity",
};

export const DIAL_HELP: Record<string, string> = {
  npc_demand_pct: "The daily NPC customer pool for player businesses, % of Pack 2 per daily player.",
  op_take_scale: "Scales every op take.",
  contract_take_scale: "Scales every Director contract take.",
  price_index: "Scales Chop prices and delivery and the market's asks (±10% at most).",
  rent_index: "Scales premises rent (±20% at most), on top of district vibe.",
  arc_sink: "How far a city arc moves rent from normal.",
};

export type ProposalStatus = "proposed" | "blocked" | "drafted" | "published" | "settled" | "reverted" | "expired";

export interface AutopilotProposal {
  id: string;
  day: string;
  dial: string;
  kind: "step" | "revert" | string;
  status: ProposalStatus | string;
  from_value: number;
  to_value: number;
  reason: Record<string, unknown> | null;
  sim: Record<string, unknown> | null;
  baseline?: number;
  evals?: unknown[] | null;
  worse_days?: number;
  base_version?: number;
  draft_version?: number | null;
  published_version?: number | null;
  published_at?: string | null;
  revert_of?: string | null;
  verdict?: "agree" | "disagree" | null;
  verdict_by?: string | null;
  verdict_note?: string | null;
  verdict_at?: string | null;
  drafted_by?: string | null;
  created_at?: string;
}

export interface AutopilotDial {
  dial: string;
  value: number;
  min: number;
  max: number;
  step: number;
  ceiling: [number, number];
  max_step: number;
  frozen: boolean;
  frozen_reason?: string | null;
  frozen_at?: string | null;
}

export interface Agreement {
  agree: number;
  disagree: number;
  pct: number;
  judged: number;
  unjudged: number;
}

export interface AutopilotRun {
  day: string;
  status: string;
  mode: string;
  signals: Record<string, unknown> | null;
  guards: string[] | null;
  notes: { dial: string; why: string }[] | null;
  catalogue_version?: number | null;
  updated_at?: string;
}

export interface AutopilotOverview {
  market: string;
  mode: "propose" | "autopublish" | string;
  autopublish_flag: boolean;
  killed: boolean;
  guards: string[];
  envelope: Record<string, unknown>;
  dials: AutopilotDial[];
  targets: Record<string, number>;
  runs: AutopilotRun[];
  proposals: AutopilotProposal[];
  agreement: Agreement;
  exit_pct: number;
}

// ---------------------------------------------------------------- helpers

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

/** A dial's value as staff read it: NPC demand in % of Pack 2, the rest as a multiplier. */
export function dialValue(dial: string, v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (dial === "npc_demand_pct") return v.toFixed(2) + "%";
  return "×" + v.toFixed(3);
}

/** A change as staff read it: "×1.000 → ×1.020 (+2.0%)", "3.00% → 2.90% (−0.10)". */
export function stepText(dial: string, from: number, to: number): string {
  const d = to - from;
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  const delta = dial === "npc_demand_pct" ? Math.abs(d).toFixed(2) + " pts" : ((Math.abs(d) / (from || 1)) * 100).toFixed(1) + "%";
  return `${dialValue(dial, from)} → ${dialValue(dial, to)} (${sign}${delta})`;
}

/** Which way a change goes: "up", "down" or "none". */
export const direction = (from: number, to: number): "up" | "down" | "none" => (to > from ? "up" : to < from ? "down" : "none");

// ---------------------------------------------------------------- targets

export interface TargetRow {
  key: string;
  label: string;
  value: number | null;
  band: string;
  inRange: boolean | null; // null: no data
}

const pct = (v: number) => (v * 100).toFixed(1) + "%";

/** The APL-01 targets from a run's signals against the bands. */
export function targetRows(signals: unknown, targets: Record<string, number> | null | undefined): TargetRow[] {
  const s = isObj(signals) ? signals : {};
  const t = targets ?? {};
  const inRange = isObj(s.in_range) ? s.in_range : {};
  const has = (k: string) => typeof inRange[k] === "boolean";
  const row = (key: string, label: string, value: unknown, band: string): TargetRow => ({
    key,
    label,
    value: has(key) ? num(value) : null,
    band,
    inRange: has(key) ? (inRange[key] as boolean) : null,
  });
  return [
    row("burn_mint", "Burn ÷ mint (30 days)", s.burn_mint30, `${t.burn_mint_min ?? 0.85}–${t.burn_mint_max ?? 1}`),
    row("supply_growth", "Supply per weekly player, week on week", s.supply_growth, `≤ ${pct(t.supply_growth_max ?? 0.03)}`),
    row("npc_share", "NPC share of new money (7 days)", s.npc_share, `≤ ${pct(t.npc_share_max ?? 0.15)}`),
    row("payback", "Median business payback (days)", s.payback, `${t.payback_min ?? 45}–${t.payback_max ?? 180}`),
    row("consumption", "Consumption share of sinks (7 days)", s.consumption, `≥ ${pct(t.consumption_min ?? 0.25)}`),
  ];
}

/** A target's value for display. */
export function targetValue(key: string, v: number | null): string {
  if (v === null) return "—";
  if (key === "burn_mint") return v.toFixed(3);
  if (key === "payback") return v.toFixed(0) + " d";
  return pct(v);
}

// ---------------------------------------------------------------- agreement

/** Where the agreement stands against the exit (≥ 80% over two weeks, with enough judged to count). */
export function agreementState(a: Agreement | null | undefined, exitPct = 80, minJudged = 10): { pct: number; meets: boolean; enough: boolean; text: string } {
  const judged = a?.judged ?? 0;
  const p = a?.pct ?? 0;
  const enough = judged >= minJudged;
  const meets = enough && p >= exitPct;
  const text = !judged
    ? "No verdicts yet"
    : `${p.toFixed(1)}% of ${judged} judged${enough ? "" : ` (needs ${minJudged} to count)`}${a?.unjudged ? ` · ${a.unjudged} waiting` : ""}`;
  return { pct: p, meets, enough, text };
}

// ---------------------------------------------------------------- proposals

export interface ProposalActions {
  verdict: boolean; // agree / disagree
  draft: boolean; // make a catalogue draft (four-eyes publish)
  why?: string; // why drafting isn't offered
}

/** What staff can do with a proposal: judge any proposal that was on offer; draft an open one (a step on a frozen dial waits for the unfreeze). */
export function proposalActions(p: AutopilotProposal, frozen: boolean, mayDraft: boolean): ProposalActions {
  const verdict = p.status !== "blocked";
  if (p.status !== "proposed") return { verdict, draft: false, why: p.status === "drafted" ? `draft v${p.draft_version ?? "?"}` : undefined };
  if (frozen && p.kind === "step") return { verdict, draft: false, why: "dial frozen" };
  if (!mayDraft) return { verdict, draft: false, why: "needs economy.draft" };
  return { verdict, draft: true };
}

export const STATUS_TONE: Record<string, string> = {
  proposed: "violet",
  blocked: "danger",
  drafted: "warn",
  published: "ok",
  settled: "info",
  reverted: "danger",
  expired: "",
};

export const STATUS_TEXT: Record<string, string> = {
  proposed: "Waiting for staff (propose-only) or for the publish step.",
  blocked: "The simulator or the catalogue checks said no.",
  drafted: "A catalogue draft waits for its four-eyes publish.",
  published: "Live; watched for 7 days.",
  settled: "Live for 7 days without making its target worse.",
  reverted: "Undone after its target got worse two days running.",
  expired: "Out of date (a newer proposal, or the dial moved).",
};

/** The controller's terms behind a proposal, as chips: "inflation +0.40". */
export function errorTerms(reason: unknown): { key: string; value: number }[] {
  const r = isObj(reason) ? reason : {};
  const why = isObj(r.why) ? r.why : {};
  const terms = isObj(why.terms) ? why.terms : {};
  return Object.entries(terms)
    .map(([key, v]) => ({ key, value: num(v) ?? 0 }))
    .filter((x) => x.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || a.key.localeCompare(b.key));
}

/** The simulator's verdict on a proposal: pass, what failed, and the expected change in each figure. */
export function simView(sim: unknown): { pass: boolean | null; failed: string[]; expected: { key: string; value: number }[] } {
  const s = isObj(sim) ? sim : {};
  const failed = Array.isArray(s.failed) ? s.failed.filter((x): x is string => typeof x === "string") : [];
  const exp = isObj(s.expected) ? s.expected : {};
  const expected = Object.entries(exp)
    .map(([key, v]) => ({ key, value: num(v) ?? 0 }))
    .filter((x) => x.value !== 0);
  return { pass: typeof s.pass === "boolean" ? s.pass : null, failed, expected };
}

export const SIM_LABEL: Record<string, string> = {
  burn_mint: "burn ÷ mint",
  consumption: "consumption share",
  npc_share: "NPC share",
  supply_growth: "supply growth",
};

/** Normalises the overview (null arrays from the API become empty). */
export function normalizeOverview(raw: AutopilotOverview): AutopilotOverview {
  return {
    ...raw,
    guards: raw.guards ?? [],
    dials: raw.dials ?? [],
    runs: (raw.runs ?? []).map((r) => ({ ...r, guards: r.guards ?? [], notes: r.notes ?? [] })),
    proposals: raw.proposals ?? [],
    agreement: raw.agreement ?? { agree: 0, disagree: 0, pct: 0, judged: 0, unjudged: 0 },
    targets: raw.targets ?? {},
  };
}

export const RUN_TEXT: Record<string, string> = {
  ran: "Ran",
  guarded: "Stopped by guards",
  paused: "Paused (kill switch)",
  disabled: "Dial envelope off",
  insufficient_data: "Under 7 days of flows",
};

export const SKIP_TEXT: Record<string, string> = {
  frozen: "frozen",
  changed_today: "changed today",
  settling: "settling after a change",
  outside_envelope: "outside its envelope",
  no_step: "step is 0",
  at_envelope_edge: "at the envelope's edge",
  changes_per_day: "over today's changes_per_day",
};
